/** Loggen: én natt om gangen, og resten når du ber om det.
 *
 * ---------------------------------------------------------------------------
 * HVA SOM VAR GALT MED FORRIGE UTGAVE
 *
 * Fem blokker med samme visuelle vekt oppå hverandre: hvilestripe, kurve,
 * forhold, tjue filterknapper, tidsvindu. Ingenting sa hvor du skulle se
 * først, så alt måtte leses. Fjorten ruter der ni sa «100» brukte sidens
 * dyreste plass på at ingenting hadde skjedd. Og fargen gjorde motsatt jobb av
 * den den skal: merkevaren er 85 % dyp, 12 % signal, 3 % puls, mens skjermen
 * var nesten halvt signal — fordi «bra» var tegnet som en stor fylt flate.
 *
 * ---------------------------------------------------------------------------
 * HVA DEN ER NÅ
 *
 * Ett tall, stort, med en ring rundt: hvilescoren for natta. Det er det ene
 * tallet vi har som er avgrenset, daglig, høyere-er-bedre og har en historie —
 * de fire egenskapene et nøkkeltall må ha for å tåle å stå alene. Hendelser er
 * en telling uten tak; desibel er ubegrenset og trenger et forbehold. Hvile er
 * det eneste som kan bære ringen.
 *
 * Under ringen står tre tall som beskriver SAMME natt: hendelser, stille
 * timer, nærmeste båt. Så en søylerekke med nettene bak, der du trykker deg
 * inn i en. Så lista. Filtrene ligger bak én knapp, ikke tjue på skjermen.
 *
 * BRA SKAL VÆRE STILLE. En uforstyrret natt er en lav, rolig søyle og en ring
 * i merkevarens grønn. Rødt finnes bare der noe faktisk skjedde, som er den
 * ratioen merkevaren ber om og som forrige utgave brukte opp på ingenting.
 *
 * Bevegelse bærer mening, ikke pynt: ringen fylles fra null én gang så øyet
 * finner den, søylene kommer forskjøvet så rekkefølgen leses som tid, og
 * begge deler er av under `prefers-reduced-motion`.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'

import { Noekkel, Ring, useTellOpp } from '../components/Maaling'

import {
  ApiError,
  type IndexResult, type LogHour, type LogSensor,
  type LogVessel, type SiteContext, type SiteHour, type SiteIndices,
  type SiteLog, type SiteReports,
  listenAtUrl,
  reportUrl,
} from '../api'
import { HourMap } from '../components/HourMap'
import { Lydblob } from '../components/Lydblob'
import { Peiling } from '../components/Peiling'
import { Timelyd } from '../components/Timelyd'
import { ErrorBox, Rows } from '../components/bits'
import { PageHead } from '../components/Shell'
import { nb } from '../components/Varsel'
import { newPlayhead } from '../playhead'
import { useApi } from '../useApi'

/* Klokke og dato leses ALLTID i anleggets egen sone, aldri i leserens.
 *
 * En logg over et sted er ført i det stedets døgn. Da serveren bygde `day` i
 * UTC og denne siden satte overskriften i nettleserens sone, falt gruppeskillet
 * klokka 02 lokalt: hvert døgn fikk to overskrifter med samme dato, og timene
 * under den andre hørte til dagen før. Nøkkel og etikett må komme fra samme
 * klokke, og den klokka er anleggets -- `SiteLog.timezone`. */
const klokke = (epoch: number, tz?: string | null) =>
  new Date(epoch * 1000).toLocaleTimeString('nb-NO',
    { hour: '2-digit', minute: '2-digit', timeZone: tz || undefined })

const dato = (epoch: number, tz?: string | null) =>
  new Date(epoch * 1000).toLocaleDateString('nb-NO',
    { day: 'numeric', month: 'short', timeZone: tz || undefined })

/** Frekvens, kort. Tall og enhet, ingenting annet. */
function hz(f: number): string {
  if (f >= 1000) return `${nb(f / 1000, f >= 10000 ? 0 : 1)} kHz`
  return `${Math.round(f)} Hz`
}

/** Drivbåndene som ett spenn. De ligger nesten alltid inntil hverandre, så
 *  spennet er den ærlige korte formen — en liste av fem er noe leseren må
 *  sette sammen igjen selv. */
function baand(bands: number[]): string | null {
  if (!bands.length) return null
  const lo = Math.min(...bands)
  const hi = Math.max(...bands)
  return lo === hi ? hz(lo) : `${hz(lo)}–${hz(hi)}`
}

/** Avstand i den oppløsningen avstanden fortjener. 19 m og 1,3 km er
 *  forskjellen mellom langs merden og et skip i horisonten. */
function avstand(v: Pick<LogVessel, 'closest_m' | 'distance_km'>): string {
  if (v.closest_m != null) {
    return v.closest_m < 1000 ? `${Math.round(v.closest_m)} m`
      : `${nb(v.closest_m / 1000, 1)} km`
  }
  return v.distance_km == null ? '–' : `${nb(v.distance_km, 1)} km`
}

/** Registerets hendelsestyper, på norsk. Ukjente vises som de kommer heller
 *  enn å bli skjult — en ny type fra registeret er noe en leser skal se, ikke
 *  noe denne filen skal svelge fordi den ikke kjente ordet. */
const DRIFT: Record<string, string> = {
  delousing: 'avlusing',
  delousing_suspected: 'antatt avlusing',
  harvest: 'slakting',
  harvest_suspected: 'antatt slakting',
  smolt_delivery: 'smoltutsett',
  treatment: 'behandling',
}

const driftNavn = (k: string) => DRIFT[k] ?? k.replace(/_/g, ' ')

/* --------------------------------------------------------------------- målet
 *
 * INGEN STRAFF. En skritteller sier aldri at du gikk for lite; den sier hvor
 * langt du gikk, og den feirer dagen du kom over målet. Forrige utgave gjorde
 * det motsatte: en dårlig natt ble en rød ring og ordet «forstyrret», som er
 * en dom over noe kunden ikke rår over -- båttrafikken. Å farge den rødt gjør
 * hverken laksen roligere eller kunden klokere, og det gjør at siden bare er
 * hyggelig å åpne de nettene ingenting skjedde.
 *
 * Så: ringen er alltid grønn og alltid like tykk. En urolig natt er en KORT
 * ring, ikke en rød en. Det er den samme avlesningen -- du ser med en gang at
 * det manglet noe -- uten at fargen legger en verdivurdering oppå tallet.
 *
 * Rødt og gult finnes fortsatt på siden, men bare der noe faktisk HENDTE: i
 * spekteret, i blobben, i hendelsestellingen. Det er merkevarens ratio, og det
 * er også den ærlige plasseringen -- en farge som betyr «her skjedde noe» kan
 * ikke samtidig bety «dette var en dårlig natt».
 */

/** Nådd mål. 80 av 100 er ikke et fysisk skille -- det er terskelen der en natt
 *  har vært rolig nok til at det er verdt å telle den, valgt som en
 *  skritteller velger ti tusen skritt: høyt nok til å bety noe, lavt nok til
 *  at det skjer ofte. Nettene over målet er det siden feirer. */
const MAAL = 80

/** Hvor mange netter på rad, fra den siste og bakover, som nådde målet.
 *
 *  Bakover fra slutten og ikke den lengste serien i vinduet: en serie er noe
 *  som løper NÅ, og «lengste noensinne» er en annen og mye svakere følelse. */
function serie(netter: IndexResult[]): number {
  let n = 0
  for (let i = netter.length - 1; i >= 0; i--) {
    const s = netter[i]!.score
    if (s === null) continue      // en natt uten måling bryter ikke en serie
    if (s < MAAL) break
    n++
  }
  return n
}

/* ------------------------------------------------------------------ filtre
 *
 * Filtrene ER produktet. En logg over fjorten dager er hundre og førti timer,
 * og hundre og førti av noe er en liste — ikke et instrument. Instrumentet
 * oppstår i det en leser kan spørre «hvilke av disse hadde ingen båt i
 * nærheten», som er det ene spørsmålet loggen kan svare på og ingen andre
 * kilder kan.
 *
 * Filtrering skjer i nettleseren, over vinduet som allerede er hentet. Det er
 * ikke en snarvei: det er det som gjør at tellingene kan oppdatere seg mens du
 * velger, og en telling som endrer seg er hele forskjellen mellom et filter du
 * tør bruke og et du gjetter på. Tjeneren har de samme parametrene for den som
 * spør den direkte.
 *
 * Innenfor en gruppe er valgene ELLER, mellom grupper OG. Tellingen på hver
 * knapp er fasettert — den viser hva DENNE knappen ville gitt med de andre
 * gruppene som de står — så en knapp som ville tømt lista sier 0 før du
 * trykker på den framfor etterpå.
 */

type Gruppe = 'retning' | 'styrke' | 'baand' | 'fartoy' | 'tid' | 'sensor'

interface Filter {
  retning: string[]
  styrke: string[]
  baand: string[]
  fartoy: string[]
  tid: string[]
  sensor: string[]
  /** Satt ved klikk i hvilestripa. Én natt, ikke et filter du skriver inn. */
  natt: [number, number] | null
}

const TOMT: Filter = { retning: [], styrke: [], baand: [], fartoy: [],
                       tid: [], sensor: [], natt: null }

const antallValgt = (f: Filter) =>
  f.retning.length + f.styrke.length + f.baand.length + f.fartoy.length
  + f.tid.length + f.sensor.length + (f.natt ? 1 : 0)

/** Båndgruppene. Grensene er akustiske, ikke pene tall.
 *
 *  100 Hz skiller maskineri og skrogstøy fra alt annet. 1 kHz skiller det
 *  midtre feltet fra regn, fôring og klikk. Laksens hørsel ligger i sin helhet
 *  under 400 Hz, som er grunnen til at det høye feltet kan være det høyeste
 *  som er målt og likevel ikke er noe fisken hører. */
function baandGruppe(bands: number[]): string | null {
  if (!bands.length) return null
  const m = bands.reduce((a, b) => a + b, 0) / bands.length
  return m < 100 ? 'lav' : m < 1000 ? 'mid' : 'hoy'
}

/** Nærmeste fartøy i meter, eller uendelig. */
const naermest = (t: LogHour) =>
  t.vessels.reduce((a, v) => Math.min(a, v.closest_m ?? (v.distance_km ?? 99) * 1000),
                   Infinity)

function verdien(t: LogHour, g: Gruppe, natt: (h: number) => boolean | null): string[] {
  switch (g) {
    case 'retning': return [t.direction === 'quieter' ? 'ned' : 'opp']
    case 'styrke': return [t.verdict === 'event' ? 'hendelse' : 'forhoyet']
    case 'baand': { const b = baandGruppe(t.driver_bands_hz); return b ? [b] : [] }
    case 'fartoy': {
      const d = naermest(t)
      /* Tre svar, ikke to. «Ingen båt» er den interessante resten — timene
         registeret ikke kan forklare — og den forsvinner i en ja/nei-deling
         der en båt to kilometer unna teller som en båt. */
      return [d <= 500 ? 'naer' : d < Infinity ? 'fjern' : 'ingen']
    }
    /* Tre svar igjen, ikke to. Vet vi ikke om timen var natt, sier vi ingen
       ting -- da faller den ut av BÅDE «natt» og «dag» framfor å bli talt som
       dag fordi sola manglet. Et filter som later som det vet, er verre enn et
       som er tomt. */
    case 'tid': {
      const n = natt(t.hour_epoch)
      return n === null ? [] : [n ? 'natt' : 'dag']
    }
    case 'sensor': return t.sensors
      .filter((s) => s.verdict === 'event' || s.verdict === 'elevated')
      .map((s) => s.mission_id)
  }
}

/** Passerer raden alle gruppene bortsett fra den som telles? */
function passerer(t: LogHour, f: Filter, natt: (h: number) => boolean | null,
                  utenom?: Gruppe): boolean {
  /* Den valgte natta gjelder ALLTID, også når en telling regnes ut. `utenom`
     tar bort gruppen som telles, og natta er ikke en av gruppene -- den er et
     eget valg gjort i stripa. Å hoppe over den her ville gitt knapper som
     lovet treff utenfor natta du står i. */
  if (f.natt
      && !(t.hour_epoch >= f.natt[0] && t.hour_epoch < f.natt[1])) return false
  const grupper: Gruppe[] = ['retning', 'styrke', 'baand', 'fartoy', 'tid', 'sensor']
  for (const g of grupper) {
    if (g === utenom) continue
    const valgt = f[g]
    if (!valgt.length) continue
    const har = verdien(t, g, natt)
    if (!har.some((v) => valgt.includes(v))) return false
  }
  return true
}


/* ------------------------------------------------------------------ vinduet
 *
 * Tre vinduer over samme måling. Natt er hvile og beholder det argumentet; dag
 * og døgn bruker den identiske målingen over andre timer og slipper
 * hvilepåstanden, fordi laks er aktiv om dagen og lyd da er eksponering
 * framfor avbrutt hvile.
 *
 * Hvert vindu måles mot SIN EGEN stille time. Arbeidsdagen her ligger omtrent
 * 15 dB over natta, så en dag målt mot netter ville kommet ut forstyrret hver
 * eneste dag. Det er analysatoren som holder orden på det; her velges bare
 * hvilket av de tre som vises. */

const VINDU = [
  { id: 'rest', kort: 'Natt', naa: 'i natt' },
  { id: 'rest_day', kort: 'Dag', naa: 'i dag' },
  { id: 'rest_24h', kort: 'Døgn', naa: 'siste døgn' },
] as const

type VinduId = typeof VINDU[number]['id']

/* ------------------------------------------------------------------- ringen */

/** Hvilescoren som ring. Ett tall, og formen sier hvor i skalaen det ligger.
 *
 *  Fylles fra null én gang ved første tegning. Det er den ene bevegelsen på
 *  siden som bærer mening framfor pynt: øyet finner tallet, og retningen sier
 *  «dette fylles opp mot hundre» uten at et ord brukes på det. Av under
 *  `prefers-reduced-motion`, som resten av produktet. */
/* -------------------------------------------------------------------- hero */

/* GRADTEKST er borte med vilje. Tjeneren sender fortsatt `grade`, og den er
   riktig -- men «forstyrret» som overskrift på en natt er en dom over
   båttrafikk kunden ikke rår over, og den gjorde siden til noe som bare var
   hyggelig å åpne de nettene ingenting hadde skjedd. `oppnaadd()` under sier
   det samme tallet den andre veien. */

/** Natta som noe som ble oppnådd, ikke som en karakter.
 *
 *  «7 av 9 timer rolige» og «forstyrret» beskriver samme natt. Det første er et
 *  tall leseren kan gjøre noe med og som teller det som gikk BRA; det andre er
 *  en dom. Begge er sanne, og bare det ene får noen til å komme tilbake i
 *  morgen. Hendelsene forsvinner ikke -- de står som et tall rett under, med
 *  sin egen farge -- men de er ikke overskriften. */
function oppnaadd(stille: number, maalte: number): string {
  if (maalte <= 0) return ''
  if (stille >= maalte) return 'hele natta rolig'
  return `${stille} av ${maalte} timer rolige`
}

const AARSAK: Record<string, string> = {
  no_dark_period: 'ingen natt på denne breddegraden nå',
  insufficient_coverage: 'for lite av natta ble tatt opp',
  no_baseline: 'for kort historikk her ennå',
  sensor_cannot_hear_enough_of_the_band: 'hydrofonen hører for lite av båndet',
}

function Hero({ natt, timer, valgt, naa, rad, beste, roligeTotalt, dager,
                aapne }: {
  natt: IndexResult | null
  /** Timene i loggen som faller innenfor perioden. */
  timer: LogHour[]
  valgt: boolean
  /** «i natt» · «i dag» · «siste døgn» */
  naa: string
  /** Netter på rad som nådde målet, akkurat nå. */
  rad: number
  /** Om denne natta er den beste i vinduet. */
  beste: boolean
  /** Rolige timer i hele vinduet, og hvor langt vinduet er. Telleren som
   *  alltid finnes -- se kommentaren ved nøkkeltallet. */
  roligeTotalt: number
  dager: number
  /** Åpner timen et nøkkeltall kom fra. «Nærmeste båt» er tallet kartet
   *  forklarer, så det er veien inn dit -- ellers er kartet noe du må lete
   *  etter framfor noe et tall peker på. */
  aapne: (h: number) => void
}) {
  const vist = useTellOpp(natt?.score ?? null)
  if (!natt) return null

  const score = natt.score
  const hendelser = timer.filter((t) => t.verdict === 'event').length
  const maalte = Number(natt.detail?.hours_measured ?? 0)
  const stille = Math.max(0, maalte - timer.length)
  const baat = timer.reduce((a, t) => Math.min(a, naermest(t)), Infinity)
  const baatTime = timer.find((t) => naermest(t) === baat)


  return (
    <div className="hero">
      <div className="hero-ring">
        <Ring maal={MAAL} score={score} />
        <div className="hero-tall">
          <strong>{vist === null ? '·' : Math.round(vist)}</strong>
        </div>
      </div>

      <div className="hero-tekst">
        <span className="hero-naar">
          {valgt ? dagKort(natt.period) : naa}
        </span>
        <span className="hero-grad">
          {score === null ? (AARSAK[natt.reason] ?? natt.reason)
            : oppnaadd(stille, maalte)}
        </span>
      </div>

      {/* Det som er tjent opp. Ingen av disse kan bli negative: en serie som
          brytes forsvinner, den blir ikke til en rød null. Det er hele
          forskjellen mellom en teller og en karakterbok. */}
      {(rad >= 2 || beste || (score !== null && score >= MAAL)) && (
        <div className="merker">
          {score !== null && score >= MAAL && (
            <span className="merke naadd">mål nådd</span>
          )}
          {rad >= 2 && <span className="merke">{rad} netter på rad</span>}
          {beste && <span className="merke">beste i perioden</span>}
        </div>
      )}

      {score !== null && (
        <div className="noekkel">
          <Noekkel verdi={String(hendelser)} merke="hendelser"
                   varsel={hendelser > 0} />
          {/* «stille» stod her og sa 11 t rett under en linje som sa «11 av 18
              timer rolige» -- samme tall, to ganger, ved siden av hverandre.
              Erstattet av det som faktisk manglet: SUMMEN.

              En skritteller har alltid noe å vise fram, også på en dag du ikke
              gikk noe sted, fordi uka fortsatt teller. Ringen og merkene er
              tomme på en natt som 43 uten serie -- og en belønning som bare
              finnes når det gikk bra, er ingen belønning. Dette tallet kan
              bare vokse. */}
          <Noekkel verdi={String(roligeTotalt)} enhet="t"
                   merke={`rolige, ${dager} d`} />
          <Noekkel verdi={baat === Infinity ? '–'
            : baat < 1000 ? String(Math.round(baat)) : nb(baat / 1000, 1)}
            enhet={baat === Infinity ? undefined : baat < 1000 ? 'm' : 'km'}
            merke="nærmeste båt"
            aapne={baatTime ? () => aapne(baatTime.hour_epoch) : undefined} />
        </div>
      )}
    </div>
  )
}

/* ------------------------------------------------------------------ nettene */

/** Nettene bak, som søyler. Høy søyle er en god natt.
 *
 *  Høyden er scoren, ikke dens motsatte, så en dårlig natt er et hull i rekka
 *  framfor en tapp som stikker opp. Det er samme avlesning en skritteller gir:
 *  du leter etter dagene som mangler, ikke etter dem som er der.
 *
 *  Ingen tall i søylene. Tallet står i ringen, for den natta du har valgt. */
function Netter({ netter, valgt, velg, dager, settDager }: {
  netter: IndexResult[]
  valgt: string | null
  velg: (n: IndexResult | null) => void
  dager: number
  settDager: (d: number) => void
}) {
  const [inne, setInne] = useState(false)
  useEffect(() => {
    const rolig = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    if (rolig) { setInne(true); return }
    const id = requestAnimationFrame(() => setInne(true))
    return () => cancelAnimationFrame(id)
  }, [netter.length])

  if (!netter.length) return null

  return (
    <div className="netter">
      <div className="netter-rad">
        {/* Målet, som en strek tvers over. Samme tall som hakket i ringen, så
            de to figurene på siden måler mot det samme. */}
        <span className="netter-maal" style={{ bottom: `${(MAAL / 100) * 46}px` }} />
        {netter.map((n, i) => {
          const paa = n.period === valgt
          const h = n.score === null ? 6 : Math.max(6, (n.score / 100) * 46)
          return (
            <button key={n.period}
              className={`natt${paa ? ' valgt' : ''}`}
              aria-pressed={paa}
              aria-label={`${dagKort(n.period)}: ${n.score === null ? 'ukjent'
                : Math.round(n.score)}${n.score !== null && n.score >= MAAL
                  ? ', mål nådd' : ''}`}
              onClick={() => velg(paa ? null : n)}>
              {/* Prikken over søyla er nettene som nådde målet. Det er det
                  eneste øyet skal lete etter i denne rekka -- ingen farge, bare
                  et merke, som en ring som er lukket. */}
              <span className={`natt-naadd${
                n.score !== null && n.score >= MAAL ? ' paa' : ''}`} />
              <span className={`natt-soyle${n.score === null ? ' tom' : ''}`}
                style={{
                  height: inne ? `${h}px` : '2px',
                  transitionDelay: `${Math.min(i * 18, 320)}ms`,
                }} />
            </button>
          )
        })}
      </div>
      <div className="netter-akse">
        <span>{dagKort(netter[0]!.period)}</span>
        <div className="seg sm stille" role="tablist">
          {[7, 14, 30].map((d) => (
            <button key={d} role="tab" aria-selected={d === dager}
              onClick={() => settDager(d)}>{d} d</button>
          ))}
        </div>
        <span>{dagKort(netter[netter.length - 1]!.period)}</span>
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------- siden */

export function SiteLog({ id }: { id: string }) {
  const [dager, setDager] = useState(14)
  const [vindu, setVindu] = useState<VinduId>('rest')
  const [aapen, setAapen] = useState<number | null>(null)
  const [straks, setStraks] = useState<number | null>(null)
  const [f, setF] = useState<Filter>(TOMT)
  const [filterAapent, setFilterAapent] = useState(false)
  /* Begge seksjonene står åpne til leseren lukker dem. Rapportene ligger
     øverst fordi de er få og oppsummerer; hendelseslista er lang og kan
     legges vekk når den ikke er det man er her for. */
  const [apenRapporter, setApenRapporter] = useState(true)
  const [apenHendelser, setApenHendelser] = useState(true)

  const sted = useApi<{ name: string }>(`/v1/sites/${encodeURIComponent(id)}`)
  const logg = useApi<SiteLog>(
    `/v1/sites/${encodeURIComponent(id)}/log?days=${dager}`)
  const ktx = useApi<SiteContext>(
    `/v1/sites/${encodeURIComponent(id)}/context?days=${dager}`)
  const idx = useApi<SiteIndices>(
    `/v1/sites/${encodeURIComponent(id)}/indices?days=${dager}`)

  /* Var timen natt?
   *
   * HVORFOR HVILEPERIODENE OG IKKE SOLSERIEN. Natt er et regnestykke på
   * breddegrad og tid -- rein geometri, som ikke kan bli utilgjengelig. Den
   * forrige utgaven spurte likevel en HENTET serie, `sun_altitude_deg` i
   * kontekst-endepunktet, og falt tilbake på `?? 90` -- høy sol -- når et punkt
   * manglet. Da kontekstjobben stanset 23. august 15:17, ble hver time etter
   * det stille omgjort til «dag», og nattfilteret sluttet å finne netter i
   * akkurat det døgnet noen ville se på. En avlesning som svarer «dag» når den
   * ikke vet noe, er en feil som ser ut som et tomt søk.
   *
   * Hvilescorens perioder kommer fra `analyzer/domain/solar.py`, regnet ut fra
   * anleggets egen posisjon, og siden henter dem allerede. De er fasit her.
   * Solserien er bare reserve, og NULL -- «vet ikke» -- er svaret når ingen av
   * dem dekker timen. */
  const natt = useMemo(() => {
    const perioder = (idx.data?.results ?? [])
      .filter((r) => r.index === 'rest' && r.start && r.end)
      .map((r) => [Date.parse(r.start) / 1000, Date.parse(r.end) / 1000])
      .filter(([a, b]) => Number.isFinite(a) && Number.isFinite(b))
      .sort((x, y) => x[0]! - y[0]!)

    if (perioder.length) {
      const fra = perioder[0]![0]!
      const til = perioder[perioder.length - 1]![1]!
      return (h: number): boolean | null => {
        /* Etter timens MIDTPUNKT, som i `analyzer/indices/rest.py`, så en time
           havner i nøyaktig én periode og ingen telles i to. */
        const senter = h + 1800
        if (perioder.some(([a, b]) => senter >= a! && senter < b!)) return true
        /* Utenfor en natt er dag -- men bare der nettene faktisk rekker. Før
           første skumring og etter siste gry vet vi ingen ting. */
        return senter >= fra && senter < til ? false : null
      }
    }

    const sol = ktx.data?.series.find((x) => x.metric === 'sun_altitude_deg')
    if (!sol?.points.length) return () => null
    const kart = new Map(sol.points.map(([t, v]) => [Math.floor(t / 3600), v]))
    return (h: number): boolean | null => {
      const v = kart.get(Math.floor(h / 3600))
      return v === undefined ? null : v < -6
    }
  }, [idx.data, ktx.data])

  /* Én rad per natt, verste hydrofon vinner. En forstyrrelse hørt på én
     hydrofon skjedde på anlegget; å midle den bort med fem stille er å
     rapportere gjennomsnittet av et sted framfor det som foregikk i det. */
  const netter = useMemo(() => {
    const rader = (idx.data?.results ?? []).filter((r) => r.index === vindu)
    const per = new Map<string, IndexResult>()
    for (const r of rader) {
      const har = per.get(r.period)
      if (!har || har.score === null
          || (r.score !== null && r.score < har.score)) per.set(r.period, r)
    }
    return [...per.values()].sort((a, b) => a.period.localeCompare(b.period))
  }, [idx.data, vindu])

  /* Ringen viser den valgte natta, ellers den siste som har en score. */
  const sisteMedScore = [...netter].reverse().find((n) => n.score !== null)
  const visNatt = useMemo(() => {
    if (!f.natt) return sisteMedScore ?? netter[netter.length - 1] ?? null
    return netter.find((n) =>
      Math.floor(new Date(n.start).getTime() / 1000) === f.natt![0]) ?? null
  }, [f.natt, netter, sisteMedScore])

  const alle = logg.data?.hours ?? []
  /* Anleggets sone, ikke leserens. `undefined` lar `toLocale*` falle tilbake på
     nettleseren, som er riktig svar bare når anlegget ikke har en sone satt. */
  const tz = logg.data?.timezone ?? undefined
  const valgte = useMemo(
    () => alle.filter((t) => passerer(t, f, natt)), [alle, f, natt])

  /* Timene som hører til natta ringen viser, uavhengig av filteret --
     nøkkeltallene skal beskrive natta, ikke utvalget. */
  const nattTimer = useMemo(() => {
    if (!visNatt) return []
    const fra = Math.floor(new Date(visNatt.start).getTime() / 1000)
    const til = Math.ceil(new Date(visNatt.end).getTime() / 1000)
    return alle.filter((t) => t.hour_epoch >= fra && t.hour_epoch < til)
  }, [alle, visNatt])

  const sterkeste = useMemo(
    () => valgte.reduce<LogHour | null>(
      (a, t) => (a === null || (t.score ?? 0) > (a.score ?? 0) ? t : a), null),
    [valgte])

  if (logg.cold) return <Rows n={6} />

  if (logg.error instanceof ApiError && logg.error.code === 'not_entitled') {
    return (
      <>
        <PageHead title="Logg" tight />
        <div className="notice">Loggen er ikke en del av abonnementet her.</div>
      </>
    )
  }
  if (logg.error) return <ErrorBox error={logg.error} />
  const d = logg.data
  if (!d) return null
  if (!d.analysed) {
    return (
      <>
        <PageHead title="Logg" tight />
        <div className="notice">Ingen analyse ennå.</div>
      </>
    )
  }

  const valgtAntall = antallValgt(f)

  return (
    <>
      <PageHead
        title={sted.data?.name ?? 'Logg'}
        tight
        lead={`${d.coverage.sensors_reporting}/${d.coverage.sensors} hydrofoner`}
        cta={sterkeste && (
          <button className="btn primary"
            onClick={() => {
              setApenHendelser(true)
              setAapen(sterkeste.hour_epoch)
              setStraks(sterkeste.hour_epoch)
              requestAnimationFrame(() => document
                .getElementById(`t${sterkeste.hour_epoch}`)
                ?.scrollIntoView({ block: 'center', behavior: 'smooth' }))
            }}>
            Lytt {klokke(sterkeste.hour_epoch, tz)} {dato(sterkeste.hour_epoch, tz)}
          </button>
        )}
      />

      <div className="vindu-valg seg stille" role="tablist">
        {VINDU.map((v) => (
          <button key={v.id} role="tab" aria-selected={v.id === vindu}
            onClick={() => { setVindu(v.id); setF({ ...f, natt: null }) }}>
            {v.kort}
          </button>
        ))}
      </div>

      <Hero natt={visNatt} timer={nattTimer} valgt={f.natt !== null}
            naa={VINDU.find((v) => v.id === vindu)!.naa}
            rad={serie(netter)}
            beste={visNatt?.score != null && netter.every(
              (n) => n.score === null || n.score <= visNatt.score!)
              && netter.filter((n) => n.score !== null).length > 1}
            roligeTotalt={Math.max(0, netter.reduce(
              (a, n) => a + Number(n.detail?.hours_measured ?? 0), 0)
              - alle.length)}
            dager={dager}
            aapne={(h) => {
              setApenHendelser(true)
              setAapen(h)
              setStraks(null)
              requestAnimationFrame(() => document.getElementById(`t${h}`)
                ?.scrollIntoView({ block: 'center', behavior: 'smooth' }))
            }} />

      <Netter netter={netter} dager={dager} settDager={setDager}
              valgt={f.natt ? (visNatt?.period ?? null) : null}
              velg={(n) => setF({
                ...f,
                natt: n === null ? null
                  : [Math.floor(new Date(n.start).getTime() / 1000),
                     Math.ceil(new Date(n.end).getTime() / 1000)],
              })} />

      <Seksjon tittel="Rapporter" apen={apenRapporter}
               veksle={() => setApenRapporter(!apenRapporter)}>
        <Rapporter id={id} />
      </Seksjon>

      <Seksjon tittel="Hendelser" tall={valgte.length} apen={apenHendelser}
               veksle={() => setApenHendelser(!apenHendelser)}>
        <div className="verktoy">
          <button className={`fchip${filterAapent ? ' paa' : ''}`}
            aria-expanded={filterAapent}
            onClick={() => setFilterAapent(!filterAapent)}>
            Filtrer{valgtAntall > 0 && <i>{valgtAntall}</i>}
          </button>
          <span className="verktoy-tall">
            {valgte.length}{valgte.length !== alle.length && ` / ${alle.length}`}
          </span>
        </div>

        {filterAapent && (
          <Filterlinje rader={alle} f={f} sett={setF} natt={natt}
                       nullstill={valgtAntall > 0 ? () => setF(TOMT) : undefined} />
        )}

        <Hendelser rader={valgte} id={id} aapen={aapen} tz={tz} velg={(h) => {
          setAapen(h); setStraks(null)
        }} straks={straks} />
      </Seksjon>

      <Tegnforklaring idx={idx.data ?? null} ktx={ktx.data ?? null} />
    </>
  )
}

/* ------------------------------------------------------------------ seksjon */

/* En del av siden som kan lukkes.
 *
 * Loggen har fått to ting som begge vil ha plass: hendelseslista, som er lang,
 * og rapportene, som er få og viktige. Uten en måte å lukke lista på ville
 * rapportene alltid ligge under et par skjermhøyder med timer. Så begge er
 * seksjoner med samme oppførsel, og leseren bestemmer hvilken som står åpen.
 *
 * Knappen er en <button> med aria-expanded, ikke en <div> med onClick: det er
 * det som gjør at tastatur og skjermleser oppfører seg som forventet uten at
 * vi må bygge det på nytt. */
function Seksjon({ tittel, tall, apen, veksle, children }: {
  tittel: string
  /** Antallet i seksjonen, når det finnes et. Null skjuler merket. */
  tall?: number | null
  apen: boolean
  veksle: () => void
  children: ReactNode
}) {
  const id = `seksjon-${tittel.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`
  return (
    <section className={`seksjon${apen ? ' apen' : ''}`}>
      <button className="seksjon-hode" aria-expanded={apen} aria-controls={id}
              onClick={veksle}>
        <span className="seksjon-tittel">{tittel}</span>
        {tall != null && <span className="seksjon-tall">{tall}</span>}
        <span className="seksjon-pil" aria-hidden="true" />
      </button>
      <div className="seksjon-kropp" id={id} hidden={!apen}>{children}</div>
    </section>
  )
}

/* ---------------------------------------------------------------- rapporter */

/** Filstørrelse i den oppløsningen en leser trenger: hele megabyte, eller kB
 *  under én. Desimalkomma, som alt annet med tall i denne appen. */
function stor(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} kB`
  return `${(bytes / 1024 / 1024).toFixed(1).replace('.', ',')} MB`
}

function Rapporter({ id }: { id: string }) {
  const q = useApi<SiteReports>(`/v1/sites/${encodeURIComponent(id)}/reports`)
  const rapporter = q.data?.reports ?? []

  if (q.cold) return <Rows n={2} />
  if (!rapporter.length) {
    return <p className="rapport-tom">Ingen rapporter er publisert for denne
      lokaliteten ennå.</p>
  }
  return (
    <ul className="rapport-liste">
      {rapporter.map((r) => (
        <li key={r.slug} className="rapport">
          <span className="rapport-ark" aria-hidden="true"><span>PDF</span></span>
          <span className="rapport-tekst">
            {/* Hele kortet er lenken, via ::after over flaten. Knappene ligger
                over igjen, så «Last ned» ikke blir spist av «Åpne». */}
            <a className="rapport-lenke" href={reportUrl(id, r.slug)}
               target="_blank" rel="noreferrer">
              <span className="rapport-navn">{r.title}</span>
              {r.subtitle && <span className="rapport-under">{r.subtitle}</span>}
            </a>
            <span className="rapport-meta">
              {new Date(r.report_date).toLocaleDateString('nb-NO',
                { day: 'numeric', month: 'long', year: 'numeric' })}
              {' · '}PDF{' · '}{stor(r.size_bytes)}
            </span>
          </span>
          <span className="rapport-handling">
            {/* Lyden først. En rapport om et tidspunkt er verdt mer når
                leseren kan høre øyeblikket enn når hun må lete etter det, og
                dette er det eneste stedet i produktet der de to møtes.
                Sekundær, ikke primær: den som åpner loggen for å lese en
                rapport skal fortsatt lande på «Åpne». */}
            {r.listen_at && (
              <a className="btn sm rapport-hor" href={listenAtUrl(id, r.listen_at, r.listen_mission)}>
                <span className="rapport-play" aria-hidden="true" />
                Hør {new Date(r.listen_at).toLocaleTimeString('nb-NO',
                  { hour: '2-digit', minute: '2-digit' })}
              </a>
            )}
            <a className="btn sm" href={reportUrl(id, r.slug, true)}>Last ned</a>
            <a className="btn primary sm" href={reportUrl(id, r.slug)}
               target="_blank" rel="noreferrer">Åpne</a>
          </span>
        </li>
      ))}
    </ul>
  )
}

/* -------------------------------------------------------------- filterlinje */

const ETIKETT: Record<Gruppe, [string, string][]> = {
  styrke: [['hendelse', 'hendelse'], ['forhoyet', 'forhøyet']],
  retning: [['opp', '↑'], ['ned', '↓']],
  baand: [['lav', '<100 Hz'], ['mid', '100 Hz–1 kHz'], ['hoy', '>1 kHz']],
  fartoy: [['naer', 'båt <500 m'], ['fjern', 'båt fjern'], ['ingen', 'ingen båt']],
  tid: [['natt', 'natt'], ['dag', 'dag']],
  sensor: [],
}

const REKKEFOELGE: Gruppe[] = ['styrke', 'retning', 'baand', 'fartoy', 'tid']

function Filterlinje({ rader, f, sett, natt, nullstill }: {
  rader: LogHour[]; f: Filter; sett: (f: Filter) => void
  /** `null` er «vet ikke om timen var natt», og skal ikke leses som «dag». */
  natt: (h: number) => boolean | null
  nullstill?: () => void
}) {
  const veksle = (g: Gruppe, v: string) => {
    const har = f[g].includes(v)
    sett({ ...f, [g]: har ? f[g].filter((x) => x !== v) : [...f[g], v] })
  }

  /* Fasettert telling: hva DENNE knappen ville gitt med de andre gruppene som
     de står. En knapp som ville tømt lista sier 0 før du trykker, ikke etter. */
  const tell = (g: Gruppe, v: string) =>
    rader.filter((t) => passerer(t, f, natt, g)
      && verdien(t, g, natt).includes(v)).length

  const sensorer = useMemo(() => {
    const m = new Set<string>()
    for (const t of rader) verdien(t, 'sensor', natt).forEach((x) => m.add(x))
    return [...m].sort()
  }, [rader, natt])

  /* Bare grupper som faktisk deler utvalget. En gruppe der alt havner i én
     bøtte er en rad knapper som ikke kan endre noe. */
  const deler = (g: Gruppe) =>
    new Set(rader.flatMap((t) => verdien(t, g, natt))).size > 1

  return (
    <div className="filter">
      {REKKEFOELGE.filter(deler).map((g) => (
        <div className="filter-rad" key={g}>
          {ETIKETT[g].map(([v, tekst]) => {
            const n = tell(g, v)
            const paa = f[g].includes(v)
            return (
              <button key={v}
                className={`fchip${paa ? ' paa' : ''}${n === 0 ? ' tom' : ''}`}
                aria-pressed={paa} onClick={() => veksle(g, v)}>
                {tekst}<i>{n}</i>
              </button>
            )
          })}
        </div>
      ))}
      {nullstill && (
        <div className="filter-rad">
          <button className="fchip nullstill" onClick={nullstill}>
            nullstill
          </button>
        </div>
      )}
      {sensorer.length > 1 && (
        <div className="filter-rad">
          {sensorer.map((m) => (
            <button key={m}
              className={`fchip${f.sensor.includes(m) ? ' paa' : ''}`}
              aria-pressed={f.sensor.includes(m)}
              onClick={() => veksle('sensor', m)}>
              {m.replace(/^msn-\d{4}-/, '').replace(/^ljsy-n-\d+$/, 'base')}
              <i>{tell('sensor', m)}</i>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

/* --------------------------------------------------------------- hendelser */

function Hendelser({ rader, id, aapen, velg, straks, tz }: {
  rader: LogHour[]; id: string
  aapen: number | null; velg: (h: number | null) => void
  straks: number | null
  /** Anleggets sone. Gruppenøkkelen `t.day` er en dato i DENNE sona, så
   *  overskriften må settes i den samme. */
  tz?: string | null
}) {
  if (!rader.length) {
    return <p className="tomt">Ingen treff.</p>
  }

  let sisteDag = ''
  return (
    <ul className="logg-liste">
      {rader.map((t) => {
        const nyDag = t.day !== sisteDag
        sisteDag = t.day
        return (
          <li key={t.hour_epoch} id={`t${t.hour_epoch}`}>
            {nyDag && <div className="logg-dag">{dato(t.hour_epoch, tz)}</div>}
            <Rad t={t} id={id} aapen={aapen === t.hour_epoch}
                 straks={straks === t.hour_epoch} tz={tz}
                 veksle={() => velg(aapen === t.hour_epoch ? null
                   : t.hour_epoch)} />
          </li>
        )
      })}
    </ul>
  )
}

/** «9. aug» uten året, fra en ren dato som `2026-08-09`.
 *
 *  Tallene plukkes ut av strengen framfor å gå via `new Date(iso)`. En ISO-dato
 *  UTEN klokkeslett tolkes av språket som midnatt i UTC, og formatteres så i
 *  leserens sone: vest for Greenwich blir «9. aug» til «8. aug». Perioden her
 *  er et navn på et døgn, ikke et øyeblikk, så den skal aldri innom en sone. */
const dagKort = (iso: string) => {
  const [aar, maaned, dag] = iso.split('-').map(Number)
  if (!aar || !maaned || !dag) return iso
  return new Date(aar, maaned - 1, dag)
    .toLocaleDateString('nb-NO', { day: 'numeric', month: 'short' })
}

function Rad({ t, id, aapen, veksle, straks, tz }: {
  t: LogHour; id: string; aapen: boolean; veksle: () => void
  /** Åpnet fra knappen øverst: last spilleren med en gang, så «Lytt» lander på
   *  en spiller framfor på en liste over minutter. */
  straks?: boolean
  /** Anleggets sone, så timen står i farmens klokke og ikke i leserens. */
  tz?: string | null
}) {
  const spenn = baand(t.driver_bands_hz)
  const sterk = t.verdict === 'event'
  const baat = t.vessels[0]

  return (
    <div className={`logg-rad${aapen ? ' aapen' : ''}`}>
      <button className="logg-sam" onClick={veksle} aria-expanded={aapen}>
        <span className="logg-tid mono">{klokke(t.hour_epoch, tz)}</span>
        {/* Retning som tegn, ikke som ord. */}
        <span className={`logg-pil ${sterk ? 'sterk' : ''}`}
              aria-label={t.direction === 'quieter' ? 'lavere' : 'høyere'}>
          {t.direction === 'quieter' ? '↓' : '↑'}
        </span>
        <span className="logg-band">{spenn}</span>
        <span className="logg-enige mono">
          {t.n_agree}/{t.n_could_hear}
        </span>
        <span className="logg-baat">
          {baat ? <>{baat.name ?? baat.mmsi}<i>{avstand(baat)}</i></> : null}
        </span>
        <span className={`logg-score mono ${sterk ? 'sterk' : ''}`}>
          {t.score === null ? '' : nb(t.score, 0)}
        </span>
      </button>
      {aapen && (
        <div className="logg-inn">
          <Time id={id} time={t.hour_epoch} straks={straks} />
        </div>
      )}
    </div>
  )
}

/* ------------------------------------------------------------------ timen */

function Time({ id, time, straks }: {
  id: string; time: number; straks?: boolean
}) {
  const q = useApi<SiteHour>(
    `/v1/sites/${encodeURIComponent(id)}/hours/${time}`)
  /* Spillehodet, delt av alle som tegner timen. Se `playhead.ts`: en vanlig
     muterbar peker framfor tilstand, fordi tre figurer tegner seksti bilder i
     sekundet og ingen av dem trenger at React vet om det. */
  const ph = useRef(newPlayhead())
  /* Blobben ber om kilden når spilleren har en. Holdt i en ref slik at
     spilleren får den samme funksjonen hver gang og lydgrafen ikke bygges om
     for at en visualisering skal montere seg. */
  const tapp = useRef<((ctx: AudioContext, kilde: AudioNode) => void) | null>(null)
  const settTapp = useCallback(
    (f: (ctx: AudioContext, kilde: AudioNode) => void) => { tapp.current = f }, [])
  const paaKilde = useCallback(
    (ctx: AudioContext, kilde: AudioNode) => tapp.current?.(ctx, kilde), [])

  if (q.cold) return <div className="logg-detalj"><Rows n={2} /></div>
  if (q.error) return <div className="logg-detalj"><ErrorBox error={q.error} /></div>
  const d = q.data
  if (!d) return null

  const fjerne = d.vessels.filter((v) => (v.closest_m ?? 1e9) > 500)
  /* Spekteret til den hydrofonen som hørte det tydeligst. */
  const sterkeste = d.sensors.reduce<LogSensor | null>(
    (a, s) => (a === null || (s.score ?? 0) > (a.score ?? 0) ? s : a), null)

  /* Kom noen nær nok til at et flyfoto viser noe? Det er den eneste testen
     satellittkartet skal bestå. En time med én båt 1,7 km unna ga en ramme på
     1,7 km åpent hav -- et svart rektangel med et rødt punkt i hjørnet og en
     etikett klippet av kanten, 210 piksler brukt på å gjenta linja under. */
  const naere = d.vessels.filter((v) => (v.closest_m ?? 1e9) <= 500)

  return (
    <div className="logg-detalj">
      {/* Timen, som en form og som et spekter. Samme tall, to lesninger:
          blobben er hvordan den kjentes, spekteret er hvor den lå. Blobben
          først fordi den treffer før den leses, og fordi den er svaret på
          «jeg hører bare vann» -- den rykker i båndet laksen hører, som er
          nettopp det båndet en laptophøyttaler ikke gjengir. */}
      <div className="timen">
        {sterkeste && <Lydblob sensor={sterkeste} ph={ph.current} tapp={settTapp} />}
        {sterkeste && <Spekter sensor={sterkeste} />}
      </div>

      {/* Hvor båtene lå, uten kart: retning som retning, avstand som avstand.
          Virker på 19 m og på 1,7 km, trenger ingen fliser, og er lesbar om
          natta -- der satellittbildet av en fjord i august er svart. */}
      {d.features?.vessels !== false && d.vessels.length > 0
        && d.site?.lat != null && d.site?.lon != null && (
        <div className="hvor">
          <Peiling senter={{ lat: d.site.lat, lon: d.site.lon }}
                   fartoy={d.vessels} ph={ph.current} />
          {/* Flyfotoet bare når noen var nær nok til at det viser noe. Da er
              det også verdt plassen: det er der en driftsleder kjenner igjen
              sin egen båt ved sine egne merder. */}
          {naere.length > 0 && (
            <HourMap senter={{ lat: d.site.lat, lon: d.site.lon }}
                     fartoy={d.vessels} ph={ph.current} hoyde={168} />
          )}
        </div>
      )}

      {d.features?.vessels !== false && d.vessels.length === 0 && (
        /* Fraværet sagt høyt. Et tomt felt leses som «ikke sjekket», og for de
           timene som ingen båt forklarer er nettopp fraværet funnet. */
        <p className="fjernt">ingen AIS-posisjon innen 2 km</p>
      )}

      {fjerne.length > 0 && naere.length > 0 && (
        <p className="fjernt">
          også innen 2 km: {fjerne.map((v) => `${v.name ?? v.mmsi} ${
            nb((v.closest_m ?? 0) / 1000, 1)} km`).join(' · ')}
        </p>
      )}

      <Forhold ktx={d.context} naar={time} />

      {d.operations.length > 0 && (
        <ul className="drift">
          {d.operations.map((o, i) => (
            <li key={i}>
              {driftNavn(o.event_type)}
              {o.vessel_name && <> · {o.vessel_name}</>}
              {o.dwell_min && <> · {Math.round(o.dwell_min / 60)} t</>}
              {!o.confirmed && <i className="antatt" title="antatt">?</i>}
            </li>
          ))}
        </ul>
      )}

      {/* Minuttene og spilleren, delt med loggboka. Blobben tegnes ikke her --
          den står oppe i `.timen` ved spekteret, som er loggens egen ramme --
          så `blob={false}` og vår `paaKilde` inn, slik at DEN blobben får tappe
          spillerens kilde. */}
      <Timelyd media={d.media} sensors={d.sensors}
        calibration={d.calibration} ph={ph.current} straks={straks}
        blob={false} onSource={paaKilde} />

    </div>
  )
}


/* ----------------------------------------------------------------- spekter */

/** Hvilke bånd som var uvanlige, som søyler.
 *
 *  DEN VIKTIGSTE RUTA PÅ SIDEN, og grunnen er ubehagelig enkel: du kan ikke
 *  høre dette. Menneskeøret faller bratt av under omtrent 100 Hz, så en
 *  skipstone på 32 Hz kan ligge 30 dB over normalen på hydrofonen og være
 *  uhørbar i en laptop. Den som trykker play hører overflatevann og
 *  konkluderer med at ingenting skjedde.
 *
 *  Spektrogrammet i spilleren under svarer på et annet spørsmål: det viser
 *  ABSOLUTT energi, som alltid domineres av vind og bølger i de høye båndene.
 *  Dette viser AVVIK fra det normale for akkurat dette stedet og denne timen,
 *  og det er der hendelsen ligger.
 *
 *  Høyden er z -- hvor uvanlig -- og ikke dB. Forskjellen er ikke akademisk:
 *  i skipstimen 9. august lå 10-20 Hz 26-32 dB over normalen med z rundt 3,
 *  mens 40 Hz lå 28 dB over med z på 23. Med dB som høyde ville de sett like
 *  viktige ut. De lave båndene svinger naturlig mye; z vet det, dB gjør ikke.
 */
function Spekter({ sensor }: { sensor: LogSensor }) {
  const [inne, setInne] = useState(false)
  useEffect(() => {
    const rolig = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    if (rolig) { setInne(true); return }
    const id = requestAnimationFrame(() => setInne(true))
    return () => cancelAnimationFrame(id)
  }, [sensor.mission_id])

  const hzs: (number | null)[] = sensor.band_centres_hz ?? []
  const zs: (number | null)[] = sensor.band_z ?? []
  const dbs: (number | null)[] = sensor.band_excess_db ?? []
  if (!hzs.length) return null

  const topp = Math.max(6, ...zs.map((z) => Math.abs(z ?? 0)))
  let verst = -1
  for (let i = 0; i < zs.length; i++) {
    if (verst < 0 || Math.abs(zs[i] ?? 0) > Math.abs(zs[verst] ?? 0)) verst = i
  }

  return (
    <div className="spekter">
      <div className="spekter-rad">
        {hzs.map((hz, i) => {
          const z = zs[i]
          const h = z == null ? 0 : (Math.abs(z) / topp) * 100
          const klasse = z == null ? 'ingen'
            : Math.abs(z) >= 6 ? 'sterk' : Math.abs(z) >= 3.5 ? 'svak' : 'rolig'
          return (
            <span key={i} className="spekter-baand"
              title={z == null ? `${hz} Hz: ikke målt`
                : `${hz && hz >= 1000 ? `${nb(hz / 1000, 1)} kHz` : `${Math.round(hz ?? 0)} Hz`}`
                  + ` · ${nb(dbs[i] ?? 0, 1)} dB over vanlig · z ${nb(z, 1)}`}>
              <i className={klasse}
                 style={{
                   height: inne ? `${h}%` : '0%',
                   transitionDelay: `${Math.min(i * 12, 260)}ms`,
                 }} />
            </span>
          )
        })}
      </div>
      {/* Der laksen hører. Én tynn strek, ingen etikett.
          Den knytter denne ruta til ringen øverst: hvilescoren er vektet med
          artens audiogram, så et avvik utenfor denne streken teller ikke inn i
          den uansett hvor rødt det er. Uten streken er de to tallene på siden
          i slekt uten at noe sier det. */}
      <div className="spekter-hoersel">
        <i style={{
          left: `${(hzs.findIndex((h) => (h ?? 0) >= 30) / hzs.length) * 100}%`,
          right: `${(1 - (hzs.findIndex((h) => (h ?? 0) > 400) < 0 ? 1
            : hzs.findIndex((h) => (h ?? 0) > 400) / hzs.length)) * 100}%`,
        }} />
      </div>

      <div className="spekter-akse">
        {/* Hvor høyt det faktisk var, når det er noe denne flåten har lov til
            å si. Tallet kommer ferdig summert fra tjeneren -- den eier
            avslaget (R5.3), og en nettleser som la sammen leddene selv ville
            kunne dikte opp et absolutt nivå av en null. Tilden og pluss/minus
            står i tallet: alle tre kalibreringsleddene her er anslag, ikke
            målinger av akkurat denne enheten. */}
        {sensor.spl_db != null ? (
          <span className="spekter-spl"
                title={`${sensor.spl_basis ?? ''} · ${
                  sensor.spl_band_hz?.[0]}–${sensor.spl_band_hz?.[1]} Hz · ${
                  sensor.spl_n_bands} bånd`}>
            ~{Math.round(sensor.spl_db)}
            {sensor.spl_uncertainty_db != null
              && ` ±${Math.round(sensor.spl_uncertainty_db)}`} dB re 1 µPa
          </span>
        ) : <span>10 Hz</span>}
        {/* Bare toppen navngis. Alle 34 ville vært en tallrad som konkurrerer
            med formen den skal forklare. */}
        {verst >= 0 && zs[verst] != null && (
          <strong>
            {(hzs[verst] ?? 0) >= 1000 ? `${nb((hzs[verst] ?? 0) / 1000, 1)} kHz`
              : `${Math.round(hzs[verst] ?? 0)} Hz`}
            {' '}{(dbs[verst] ?? 0) >= 0 ? '+' : ''}{nb(dbs[verst] ?? 0, 0)} dB
          </strong>
        )}
        <span>20 kHz</span>
      </div>
    </div>
  )
}

/** Forholdene i timen, som en linje.
 *
 *  Tidevannet kom tilbake hit og ikke til toppen av siden. Fjorten døgn med
 *  timesvis vannstand på tvers av skjermen var en smøre — kurven kunne ikke
 *  leses, og den konkurrerte med det ene tallet siden handler om. Ved siden av
 *  timen den gjelder er den to tall, og da sier den noe.
 *
 *  Det er STRØMMEN som lager lyd, ikke nivået, og de to ligger et kvart døgn
 *  fra hverandre: stillevann er ved høy- og lavvann, og strømmen er sterkest
 *  midt imellom. Derfor står raten, ikke bare centimeterne. */
function Forhold({ ktx, naar }: {
  ktx: SiteHour['context']; naar: number
}) {
  const les = (metric: string) => {
    const treff = ktx.filter((x) => x.metric === metric)
    if (!treff.length) return null
    let best = treff[0]!
    for (const p of treff) {
      if (Math.abs(p.at - naar) < Math.abs(best.at - naar)) best = p
    }
    /* En måling halvannen time unna er ikke denne timens forhold. */
    return Math.abs(best.at - naar) > 5400 ? null : best
  }

  const vann = les('water_level_cm') ?? les('tide_predicted_cm')
  const strom = les('tide_rate_cm_per_h')
  const bolge = les('wave_height_m')
  const vind = les('wind_speed_ms')
  const regn = les('precipitation_mm')

  const deler: string[] = []
  if (vann) {
    deler.push(`${Math.round(vann.value)} cm`
      + (strom ? ` ${strom.value >= 0 ? '↑' : '↓'}${
        Math.round(Math.abs(strom.value))}` : ''))
  }
  /* Modellerte tall bærer tilde. Målt og modellert er ikke samme slags tall. */
  if (bolge) deler.push(`~${nb(bolge.value, 1)} m`)
  if (vind) deler.push(`~${Math.round(vind.value)} m/s`)
  if (regn && regn.value > 0) deler.push(`~${nb(regn.value, 1)} mm/t`)

  if (!deler.length) return null
  return <p className="forhold-linje">{deler.join(' · ')}</p>
}
/* ------------------------------------------------------- tegnforklaring */

/** Lukket. Hit går en leser for å etterprøve en påstand, ikke for å lese en.
 *
 *  Forutsetningene bak hvilescoren står her og ingen andre steder på siden.
 *  De må stå: en score er en regel noen har valgt, og denne bærer en påstand
 *  om dyrevelferd som kunnskapsgrunnlaget ikke dekker. */
function Tegnforklaring({ idx, ktx }: {
  idx: SiteIndices | null; ktx: SiteContext | null
}) {
  const forutsetninger =
    (idx?.results ?? []).find((r) => r.assumptions.length)?.assumptions ?? []
  const mangler = (ktx?.providers ?? []).filter((p) => !p.ok)

  return (
    <details className="tegn">
      <summary>Tegnforklaring</summary>
      <div className="tegn-inn">
        <dl>
          <dt className="tegn-ring"><span /></dt>
          <dd>hvile for natta, 0–100. Høy søyle er en rolig natt. Hakket i
              ringen og prikken over søyla er målet, {MAAL}.</dd>
          <dt><span className="tegn-blob" /></dt>
          <dd>timen slik den kjennes for laksen: avvik fra det vanlige i
              båndet den hører, mot den stiplede sirkelen som er «vanlig»</dd>
          <dt><span className="prikk sterk" /> <span className="prikk svak" /></dt>
          <dd>hendelse · forhøyet</dd>
          <dt>↑ ↓</dt>
          <dd>høyere eller lavere enn vanlig for denne timen her</dd>
          <dt className="mono">2/5</dt>
          <dd>hydrofoner som var enige / som kunne høre båndet</dd>
          <dt>~</dt>
          <dd>modellert, ikke målt</dd>
          <dt>◆</dt>
          <dd>akvakulturflåten</dd>
          <dt>?</dt>
          <dd>antatt av registeret, ikke bekreftet</dd>
          <dt><span className="tegn-hoersel" /></dt>
          <dd>der laksen hører, 30–400 Hz. Et avvik utenfor denne streken
              teller ikke inn i hvilescoren.</dd>
          <dt>ingen båt</dt>
          <dd>ingen AIS-posisjon innenfor 2 km denne timen — ikke bevis på at
              ingen var der</dd>
          <dt>~ ±</dt>
          <dd>anslått nivå i dB re 1 µPa. Alle tre kalibreringsleddene på denne
              flåten er anslag, ikke målinger av akkurat denne enheten, så
              tallet er sammenliknbart med seg selv over tid og bærer flere dB
              systematisk usikkerhet mot andre anlegg.</dd>
        </dl>

        {forutsetninger.length > 0 && (
          <>
            <p className="tegn-tittel">Hvilescoren</p>
            <ul>{forutsetninger.map((a, i) => <li key={i}>{a}</li>)}</ul>
          </>
        )}

        {mangler.length > 0 && (
          <>
            <p className="tegn-tittel">Mangler</p>
            <ul>
              {mangler.map((p) => (
                <li key={p.provider}>{p.label}: {p.detail || p.reason}
                  {p.needs && <> — {p.needs}</>}</li>
              ))}
            </ul>
          </>
        )}
      </div>
    </details>
  )
}
